import { FieldsConfigResponse } from "@hooks/use-config-context/types";
import { TicketTableColumnFromApi } from "@hooks/use-config-context/ticket/types";
import { TableCellInputType } from "@components/data-table/types";
import {
  getFieldInputType,
  getValueFromMetaBox,
} from "@hooks/use-config/utils";
import { TICKET_KEY_FOR_ASSETS } from "@hooks/use-table-context/ticket/utils";

// These ticket "columns" aren't real ticket-table fields — they're read off
// the linked Asset object embedded in the ticket response (meta_box[TICKET_KEY_FOR_ASSETS][0]).
// The backend /aggregate/tickets endpoint can only group by real ticket columns,
// so any consumer doing server-side aggregation must special-case these ids.
export const TICKET_ASSET_DERIVED_COLUMN_IDS = [
  "asset_tag",
  "model",
  "serial_number",
  "under_warranty",
] as const;

export const getTableColumnsFromFields = (
  fields: FieldsConfigResponse,
): TicketTableColumnFromApi[] => {
  const labels = Object.values(fields).reduce((acc, field) => {
    if (field.name && field.display !== false) {
      acc.push({
        id: field.id,
        label: field.name,
        getValue: (ticket) => getValueFromMetaBox(ticket, field.id),
        inputType: getFieldInputType(field),
        clone: field.clone || false,
      });
    }
    return acc;
  }, [] as TicketTableColumnFromApi[]);

  // Add Asset Related Columns
  labels.push({
    id: "asset_tag",
    label: "Asset Tag",
    getValue: (ticket: any) =>
      ticket.meta_box?.[TICKET_KEY_FOR_ASSETS]?.[0]?.asset_tag || "-",
    inputType: TableCellInputType.SystemProvided,
    clone: false,
  });
  labels.push({
    id: "model",
    label: "Model",
    getValue: (ticket: any) =>
      ticket.meta_box?.[TICKET_KEY_FOR_ASSETS]?.[0]?.model || "-",
    inputType: TableCellInputType.SystemProvided,
    clone: false,
  });
  labels.push({
    id: "serial_number",
    label: "Serial Number",
    getValue: (ticket: any) =>
      ticket.meta_box?.[TICKET_KEY_FOR_ASSETS]?.[0]?.serial_number || "-",
    inputType: TableCellInputType.SystemProvided,
    clone: false,
  });
  labels.push({
    id: "under_warranty",
    label: "Warranty",
    getValue: (ticket: any) =>
      ticket.meta_box?.[TICKET_KEY_FOR_ASSETS]?.[0]?.under_warranty?.[0] || "-",
    inputType: TableCellInputType.SystemProvided,
    displayType: TableCellInputType.Boolean,
    clone: false,
  });

  labels.unshift({
    id: "title",
    label: "Ticket #",
    getValue: (ticket) => ticket.title.rendered,
    inputType: TableCellInputType.SystemProvided,
    clone: false,
  });
  labels.unshift({
    id: "id",
    label: "ID",
    getValue: (ticket) => ticket.id,
    inputType: TableCellInputType.SystemProvided,
    clone: false,
  });
  labels.unshift({
    id: `${TICKET_KEY_FOR_ASSETS}`,
    label: "Asset Id",
    getValue: (ticket) =>
      ticket.meta_box?.[TICKET_KEY_FOR_ASSETS]?.[0]?.id?.toString() || "",
    inputType: TableCellInputType.Form,
    clone: false,
  });

  return labels;
};
